// ./src/components/features/Highlights/HighlightsList/HighlightsList.tsx
//
// Highlights list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row (system defaults + space-
// scoped rows are read-only and not selectable), and selection-ready. Mirrors the
// Contacts list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Highlight } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalHighlightsContext } from "@/contexts/HighlightsContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface HighlightsListProps {
  data?: Highlight[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditHighlight?: (highlight: Highlight) => void;
  onDeleteHighlight?: (highlight: Highlight) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddHighlight?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (highlight: Highlight): boolean => highlight.status === STATUSES.DISABLED;

/** A row is editable when the caller has any right on it (BE sets [] for read-only rows). */
const isEditableRow = (highlight: Highlight): boolean =>
  recordHasRight(highlight, "edit") || recordHasRight(highlight, "remove");

const GROUP_COLOR: Record<string, string> = {
  amenity: "blue",
  policy: "grape",
  accessibility: "teal",
  food_drink: "orange",
  schedule: "cyan",
  entertainment: "pink",
  other: "gray",
};

const GroupBadge: React.FC<{ group?: string | null }> = ({ group }) => {
  const { t } = useTranslation(["highlights"]);

  if (!group) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color={GROUP_COLOR[group] ?? "gray"}>
      {t(`highlights:groups.${group}`, { defaultValue: group.replace(/_/g, " ") })}
    </Badge>
  );
};

const StatusBadge: React.FC<{ highlight: Highlight }> = ({ highlight }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(highlight)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// A read-only row's origin: a system default (no space) or a space-scoped one.
const ScopeBadge: React.FC<{ highlight: Highlight; editable: boolean }> = ({ highlight, editable }) => {
  const { t } = useTranslation(["highlights"]);

  if (editable) {
    return null;
  }

  const isSpace = !!highlight.spaceId;

  return (
    <Tooltip
      label={
        isSpace
          ? t("highlights:scope.spaceHint", { defaultValue: "Managed in a space — read-only here" })
          : t("highlights:scope.defaultHint", { defaultValue: "Built-in default — read-only" })
      }
      withArrow
    >
      <Badge size="xs" variant="outline" color="gray" leftSection={<AppIcon iconName="Lock" size={10} />}>
        {isSpace
          ? t("highlights:scope.space", { defaultValue: "Space" })
          : t("highlights:scope.default", { defaultValue: "Default" })}
      </Badge>
    </Tooltip>
  );
};

const HighlightIcon: React.FC<{ highlight: Highlight; size?: number }> = ({ highlight, size = 34 }) => (
  <ThemeIcon
    variant="light"
    color={GROUP_COLOR[highlight.highlightGroup ?? "other"] ?? "gray"}
    radius="xl"
    size={size}
  >
    <AppIcon iconName={highlight.icon || "Star"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  highlight: Highlight;
  onEdit?: (h: Highlight) => void;
  onDelete?: (h: Highlight) => void;
  isDeleting: boolean;
}> = ({ highlight, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(highlight, "edit");
  const showDelete = !!onDelete && recordHasRight(highlight, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(highlight)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(highlight)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  highlight: Highlight;
  name: string;
  editable: boolean;
  disabled: boolean;
  onEdit?: (h: Highlight) => void;
  onDelete?: (h: Highlight) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const HighlightTableRow: React.FC<RowProps> = ({
  highlight,
  name,
  editable,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => highlight.id && onToggleSelect?.(highlight.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <HighlightIcon highlight={highlight} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <ScopeBadge highlight={highlight} editable={editable} />
              <StatusBadge highlight={highlight} />
            </Group>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 200 }}>
        <GroupBadge group={highlight.highlightGroup} />
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(highlight.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions highlight={highlight} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const HighlightTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={18} width={90} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const HighlightCardComponent: React.FC<CardProps> = ({
  highlight,
  name,
  editable,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => highlight.id && onToggleSelect?.(highlight.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(highlight, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(highlight),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(highlight, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(highlight),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, highlight, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <HighlightIcon highlight={highlight} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge highlight={highlight} />
          </Group>
          <Group gap={6} wrap="wrap" mt={4}>
            <GroupBadge group={highlight.highlightGroup} />
            <ScopeBadge highlight={highlight} editable={editable} />
          </Group>
        </Box>
        {showActions && (
          <RowActions highlight={highlight} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const HighlightCard = React.memo(HighlightCardComponent);

const HighlightCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={2} sizes={['md', 'xs']} gap={1} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const HighlightsListComponent: React.FC<HighlightsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditHighlight,
  onDeleteHighlight,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddHighlight,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["highlights", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditHighlight, onDeleteHighlight);

  const highlightsContext = useOptionalHighlightsContext();

  const highlights = dataProp ?? highlightsContext?.highlights ?? [];
  const loading = loadingProp ?? highlightsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? highlightsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? highlightsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? highlightsContext?.loadMore;
  const isRemoving = isRemovingProp ?? highlightsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: highlights.length });

  const handleEdit = useCallback(
    (highlight: Highlight) => {
      closeAllCards();
      onEditHighlight?.(highlight);
    },
    [onEditHighlight, closeAllCards]
  );

  const handleDelete = useCallback(
    (highlight: Highlight) => {
      closeAllCards();
      onDeleteHighlight?.(highlight);
    },
    [onDeleteHighlight, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = highlightsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && highlights.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("highlights:emptyState.noMatch", { defaultValue: "No highlights match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("highlights:emptyState.title", { defaultValue: "No highlights yet" })}
          description={t("highlights:emptyState.description", {
            defaultValue: "Create a reusable highlight to feature on your events.",
          })}
          iconName="Star"
          actionLabel={t("highlights:emptyState.actionLabel", { defaultValue: "Add highlight" })}
          onAction={onAddHighlight}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (highlight: Highlight): boolean =>
    isSelectable &&
    ((!!onEditHighlight && recordHasRight(highlight, "edit")) ||
      (!!onDeleteHighlight && recordHasRight(highlight, "remove")));

  const rowProps = (highlight: Highlight): RowProps => ({
    highlight,
    name: highlight.name || "—",
    editable: isEditableRow(highlight),
    disabled: isDisabled(highlight),
    onEdit: onEditHighlight ? handleEdit : undefined,
    onDelete: onDeleteHighlight ? handleDelete : undefined,
    highlighted: highlight.id === highlightedId,
    isDeleting: isRemoving(highlight.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(highlight),
    selected: selection?.isSelected(highlight.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = highlights
    .filter((h) => rowIsSelectable(h))
    .map((h) => h.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("highlights:list.columns.name", { defaultValue: "Highlight" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>
                  {t("highlights:list.columns.group", { defaultValue: "Group" })}
                </Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("highlights:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <HighlightTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : highlights.map((highlight) => (
                    <HighlightTableRow key={highlight.id} {...rowProps(highlight)} />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <HighlightCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {highlights.map((highlight) => (
            <motion.div
              key={highlight.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <HighlightCard
                {...rowProps(highlight)}
                forceClose={shouldForceClose(highlight.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(highlight.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(highlight.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const HighlightsList = React.memo(HighlightsListComponent);
